import React, { useState, useRef, useEffect } from 'react';
import { Settings, Sun, Moon, Monitor, Check } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const THEME_OPTIONS = [
  { value: 'light', label: 'Terang', desc: 'Selalu terang', Icon: Sun },
  { value: 'dark', label: 'Gelap', desc: 'Selalu gelap', Icon: Moon },
  { value: 'system', label: 'Sistem', desc: 'Ikut perangkat', Icon: Monitor },
];

/**
 * Menu pengaturan gabungan untuk top bar (desktop) — menggantikan tombol
 * tema dan lencana bahasa yang tadinya berdiri sendiri, supaya navbar tidak
 * terlalu padat. `iconClass` menyesuaikan warna tombol dengan state navbar
 * (transparan/solid), sama seperti tombol ikon lain di navbar.
 */
export default function SettingsMenu({ iconClass = '', buttonSize = 17 }) {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="settings-menu-btn"
        onClick={() => setOpen((o) => !o)}
        className={`p-2 rounded-full transition-all ${iconClass}`}
        aria-label="Pengaturan tampilan & bahasa"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Settings size={buttonSize} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 origin-top-right z-50
            rounded-2xl border border-gray-200/80 dark:border-gray-700/70
            bg-white/95 dark:bg-[#1a1730]/95 backdrop-blur-xl
            shadow-xl shadow-black/10 dark:shadow-black/40
            p-1.5 animate-theme-pop"
        >
          <p className="px-3 pt-1.5 pb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
            Tampilan
          </p>
          {THEME_OPTIONS.map(({ value, label, desc, Icon }) => {
            const active = theme === value;
            return (
              <button
                key={value}
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  setTheme(value);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-all
                  ${active
                    ? 'bg-purple-50 dark:bg-purple-900/25'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'}`}
              >
                <span
                  className={`flex-shrink-0 grid place-items-center w-8 h-8 rounded-lg transition-colors
                    ${active
                      ? 'bg-gradient-to-br from-[#7B2FBE] to-[#9333EA] text-white shadow-sm shadow-purple-500/30'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}
                >
                  <Icon size={16} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className={`block text-sm font-semibold leading-tight
                    ${active ? 'text-[#7B2FBE] dark:text-[#b98aff]' : 'text-gray-700 dark:text-gray-200'}`}>
                    {label}
                  </span>
                  <span className="block text-[11px] text-gray-400 dark:text-gray-500 leading-tight">
                    {desc}
                  </span>
                </span>
                {active && <Check size={15} className="text-[#7B2FBE] dark:text-[#b98aff] flex-shrink-0" />}
              </button>
            );
          })}

          <div className="my-1.5 border-t border-gray-100 dark:border-gray-800" />

          <p className="px-3 pt-1 pb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
            Bahasa
          </p>
          <div className="flex items-center gap-3 px-3 py-2 rounded-xl">
            <span className="flex-shrink-0 grid place-items-center w-8 h-8 rounded-lg bg-gradient-to-br from-[#7B2FBE] to-[#9333EA] text-white text-[11px] font-bold shadow-sm shadow-purple-500/30">
              ID
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold leading-tight text-gray-700 dark:text-gray-200">
                Indonesia
              </span>
              <span className="block text-[11px] text-gray-400 dark:text-gray-500 leading-tight">
                Bahasa situs saat ini
              </span>
            </span>
            <Check size={15} className="text-[#7B2FBE] dark:text-[#b98aff] flex-shrink-0" />
          </div>
        </div>
      )}
    </div>
  );
}
